import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import usePrevious from 'components/hooks/usePrevious';

import CollectionSelect from 'components/application/CollectionSelect';
import ErrorMessage from 'components/application/ErrorMessage';
import TextArea from 'components/application/TextArea';
import TextField from 'components/application/TextField';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectCoshhContext } from 'components/contexts/ProjectCoshhContext';
import LevelOfRisk from 'components/projectCoshh/components/LevelOfRisk';

import { focusField } from 'components/helpers/refs';
import { uniquePersonsAtRiskOptions } from 'components/helpers/resources/coshhDocuments';

const exposureFrequencyOptionKeys = [
  'everyDay',
  'onceAWeek',
  'onceAMonth',
  'onceIn6Months',
  'annual',
];

const levelOfRiskOptions = ['Low', 'Medium', 'High'].map((level) => ({
  label: <LevelOfRisk option={level} />,
  value: level,
}));

const locationOptionKeys = [
  'outside',
  'insideWellVentilated',
  'insidePoorVentilation',
  'confinedSpace',
];

const methodOfUseOptionKeys = [
  'bonding',
  'cleaning',
  'coating',
  'dosing',
  'filling',
  'joining',
  'maintenance',
  'mixing',
  'painting',
  'pouring',
  'spraying',
  'sticking',
  'treating',
  'welding',
];

const workerExposureTimeOptionKeys = [
  'lessThan15Minutes',
  '15To30Minutes',
  '30MinutesTo2Hours',
  '2To4Hours',
  '4To6Hours',
  '6To8Hours',
  '8To12Hours',
];

const sidePanelSelectProps = {
  menuPlacement: 'auto',
  menuShouldScrollIntoView: false,
};

export default function CoshhForm({
  coshhDocument,
  onCoshhInputChange,
  onCoshhOptionChange,
  onOptionSelect,
  removeErrorStyling,
  requestError,
  sidePanelFieldRef,
}) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const { comparisonValueForCustomValueUsage, personsAtRiskOptions } =
    useProjectCoshhContext();

  const selectOtherFieldRef = useRef();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectCoshh.components.CoshhForm',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const {
    additionalInformation,
    exposureFrequency,
    exposureFrequencyOther,
    levelOfRisk,
    location,
    longTermExposureLimit,
    methodOfUseOther,
    personsAtRisk,
    personsAtRiskOther,
    quantityOnSite,
    quantityToBeUsed,
    shortTermExposureLimit,
    staffNumber,
    substanceQuantityPerDay,
    uses,
    workerExposureTime,
    workerExposureTimeOther,
  } = coshhDocument;

  const displayWorkerExposureTimeOtherField =
    workerExposureTime === comparisonValueForCustomValueUsage;

  const displayExposureFrequencyOtherField =
    exposureFrequency === comparisonValueForCustomValueUsage;

  const displayUsesOtherField = uses === comparisonValueForCustomValueUsage;

  const displayPersonsAtRiskOtherField = Boolean(
    personsAtRisk.find(
      (personAtRisk) =>
        personAtRisk.value === comparisonValueForCustomValueUsage,
    ),
  );

  const previousDisplayPersonsAtRiskOtherField = usePrevious(
    displayPersonsAtRiskOtherField,
  );

  const personsAtRiskOtherOptionChangesToSelected =
    previousDisplayPersonsAtRiskOtherField === false &&
    displayPersonsAtRiskOtherField;

  const otherOption = {
    label: i18nT('otherLabel'),
    value: comparisonValueForCustomValueUsage,
  };

  useEffect(() => {
    if (personsAtRiskOtherOptionChangesToSelected) {
      focusField(selectOtherFieldRef);
    }
  }, [personsAtRiskOtherOptionChangesToSelected]);

  return (
    <div className='popup__body-form popup__body-form--no-side-padding'>
      <ErrorMessage
        isFallback={requestError.isFallback}
        validationErrors={requestError.validationErrors}
        withSeparateNestedErrors={true}
        wrapperClassName='form-container'
      />
      <div className='form-container'>
        <div className='form-container-section'>
          <TextField
            customMargin='tw-mb-8'
            inputRef={sidePanelFieldRef}
            label={i18nTCommon('projects.coshh.quantityKeptOnSite')}
            maxLength={255}
            name='quantityOnSite'
            onChange={onCoshhInputChange}
            placeholder={i18nTCommon('projects.coshh.eg10Litres')}
            tooltip={i18nT('quantityOnSite.tooltip')}
            value={quantityOnSite}
          />
          <TextField
            customMargin='tw-mb-8'
            label={i18nTCommon('projects.coshh.quantityExpectedToBeUsed')}
            maxLength={255}
            name='quantityToBeUsed'
            onChange={onCoshhInputChange}
            placeholder={i18nTCommon('projects.coshh.eg10Litres')}
            tooltip={i18nT('quantityToBeUsed.tooltip', {
              interpolation: { escapeValue: false },
              singularRAMSName,
            })}
            value={quantityToBeUsed}
          />
          <TextField
            customMargin='tw-mb-8'
            label={i18nTCommon('projects.coshh.quantityUsedInOneDay')}
            maxLength={255}
            name='substanceQuantityPerDay'
            onChange={onCoshhInputChange}
            placeholder={i18nT('substanceQuantityPerDay.placeholder')}
            tooltip={i18nT('substanceQuantityPerDay.tooltip')}
            value={substanceQuantityPerDay}
          />
          <CollectionSelect
            customMargin={displayUsesOtherField ? 'tw-mb-2' : 'tw-mb-8'}
            externalProps={sidePanelSelectProps}
            isClearable={true}
            isSearchable={true}
            label={i18nTCommon('projects.coshh.methodOfUse')}
            maxMenuHeight={320}
            name='uses'
            onChange={onCoshhOptionChange}
            options={methodOfUseOptionKeys
              .map((key) => {
                const option = i18nT(`uses.options.${key}`);

                return { label: option, value: option };
              })
              .concat(otherOption)}
            placeholder={i18nT('uses.placeholder')}
            tooltip={i18nT('uses.tooltip')}
            value={uses ? { label: uses, value: uses } : ''}
          />
          {displayUsesOtherField && (
            <TextField
              fieldError={requestError.validationErrors.methodOfUseOther}
              isRequired={false}
              label=''
              maxLength={255}
              name='methodOfUseOther'
              onChange={onCoshhInputChange}
              placeholder={i18nT('methodOfUseOther.placeholder')}
              removeErrorStyling={removeErrorStyling}
              value={methodOfUseOther}
              withLabel={false}
            />
          )}
          <TextField
            customMargin='tw-mb-8'
            label={i18nTCommon(
              'projects.coshh.numberOfPeopleInvolvedInActivity',
            )}
            maxLength={255}
            name='staffNumber'
            onChange={onCoshhInputChange}
            placeholder={i18nT('staffNumber.placeholder')}
            tooltip={i18nT('staffNumber.tooltip')}
            value={staffNumber}
          />
          <CollectionSelect
            customMargin={
              displayPersonsAtRiskOtherField ? 'tw-mb-2' : 'tw-mb-8'
            }
            externalProps={{
              ...sidePanelSelectProps,
              closeMenuOnSelect: false,
              hideSelectedOptions: false,
              backspaceRemovesValue: false,
            }}
            isCheckboxes={true}
            isMulti={true}
            label={i18nTCommon('projects.coshh.personsAtRisk')}
            maxMenuHeight={320}
            name='personsAtRisk'
            onChange={onOptionSelect}
            options={uniquePersonsAtRiskOptions({
              otherOption: {
                ...otherOption,
                name: comparisonValueForCustomValueUsage,
              },
              personsAtRiskOptions,
              selectedPersonsAtRisk: personsAtRisk,
            })}
            placeholder={i18nTCommon('shared.selectPersons')}
            tooltip={i18nT('personsAtRisk.tooltip')}
            value={personsAtRisk || ''}
          />
          {displayPersonsAtRiskOtherField && (
            <TextField
              fieldError={requestError.validationErrors.personsAtRiskOther}
              inputRef={selectOtherFieldRef}
              isRequired={false}
              label=''
              maxLength={255}
              name='personsAtRiskOther'
              onChange={onCoshhInputChange}
              placeholder={i18nT('personsAtRiskOther.placeholder')}
              removeErrorStyling={removeErrorStyling}
              value={personsAtRiskOther}
              withLabel={false}
            />
          )}
          <CollectionSelect
            customMargin={
              displayWorkerExposureTimeOtherField ? 'tw-mb-2' : 'tw-mb-8'
            }
            externalProps={sidePanelSelectProps}
            isClearable={true}
            label={i18nTCommon('projects.coshh.durationOfExposure')}
            maxMenuHeight='unset'
            name='workerExposureTime'
            onChange={onCoshhOptionChange}
            options={workerExposureTimeOptionKeys
              .map((key) => {
                const option = i18nT(`workerExposureTime.options.${key}`);

                return { label: option, value: option };
              })
              .concat(otherOption)}
            placeholder={i18nT('workerExposureTime.placeholder')}
            tooltip={
              <>
                <p>{i18nT('durationOfExposureTooltip.description')}</p>
                <ul className='tw-pl-[15px]'>
                  <li className='tw-font-medium'>
                    {i18nT('durationOfExposureTooltip.longTermExposureLimit')}
                  </li>
                  <p className='tw-m-0'>
                    {longTermExposureLimit || i18nTCommon('shared.n/a')}
                  </p>
                  <li className='tw-font-medium'>
                    {i18nT('durationOfExposureTooltip.shortTermExposureLimit')}
                  </li>
                  <p className='tw-m-0'>
                    {shortTermExposureLimit || i18nTCommon('shared.n/a')}
                  </p>
                </ul>
              </>
            }
            tooltipClassName='tooltip-dark--text-left'
            value={
              workerExposureTime ?
                {
                  label: workerExposureTime,
                  value: workerExposureTime,
                }
              : ''
            }
          />
          {displayWorkerExposureTimeOtherField && (
            <TextField
              fieldError={requestError.validationErrors.workerExposureTimeOther}
              isRequired={false}
              label=''
              maxLength={255}
              name='workerExposureTimeOther'
              onChange={onCoshhInputChange}
              placeholder={i18nT('workerExposureTimeOther.placeholder')}
              removeErrorStyling={removeErrorStyling}
              value={workerExposureTimeOther}
              withLabel={false}
            />
          )}
          <CollectionSelect
            customMargin={
              displayExposureFrequencyOtherField ? 'tw-mb-2' : 'tw-mb-8'
            }
            externalProps={sidePanelSelectProps}
            isClearable={true}
            label={i18nTCommon('projects.coshh.frequencyOfExposure')}
            maxMenuHeight='unset'
            name='exposureFrequency'
            onChange={onCoshhOptionChange}
            options={exposureFrequencyOptionKeys
              .map((key) => {
                const option = i18nT(`exposureFrequency.options.${key}`);

                return { label: option, value: option };
              })
              .concat(otherOption)}
            placeholder={i18nT('exposureFrequency.placeholder')}
            tooltip={i18nT('exposureFrequency.tooltip')}
            value={
              exposureFrequency ?
                {
                  label: exposureFrequency,
                  value: exposureFrequency,
                }
              : ''
            }
          />
          {displayExposureFrequencyOtherField && (
            <TextField
              fieldError={requestError.validationErrors.exposureFrequencyOther}
              isRequired={false}
              label=''
              maxLength={255}
              name='exposureFrequencyOther'
              onChange={onCoshhInputChange}
              placeholder={i18nT('exposureFrequencyOther.placeholder')}
              removeErrorStyling={removeErrorStyling}
              value={exposureFrequencyOther}
              withLabel={false}
            />
          )}
          <CollectionSelect
            customMargin='tw-mb-8'
            externalProps={sidePanelSelectProps}
            isClearable={true}
            label={i18nTCommon('projects.coshh.locationOfUse')}
            maxMenuHeight='unset'
            name='location'
            onChange={onCoshhOptionChange}
            options={locationOptionKeys.map((key) => {
              const option = i18nT(`location.options.${key}`);

              return { label: option, value: option };
            })}
            placeholder={i18nT('location.placeholder')}
            tooltip={i18nT('location.tooltip')}
            value={location ? { label: location, value: location } : ''}
          />
          <CollectionSelect
            customMargin='tw-mb-8'
            externalProps={sidePanelSelectProps}
            isClearable={true}
            label={i18nTCommon('projects.coshh.levelOfRisk')}
            maxMenuHeight='unset'
            modifiers={['enlarged']}
            name='levelOfRisk'
            onChange={onCoshhOptionChange}
            options={levelOfRiskOptions}
            placeholder={i18nT('levelOfRisk.placeholder')}
            tooltip={i18nT('levelOfRisk.tooltip')}
            value={
              levelOfRisk ?
                {
                  label: (
                    <LevelOfRisk
                      option={levelOfRisk}
                      subtextProps={{ className: 'tw-max-w-[200px]' }}
                    />
                  ),
                  value: levelOfRisk,
                }
              : ''
            }
          />
          <TextArea
            label={i18nTCommon(
              'projects.coshh.controlMeasuresAndAdditionalInformation',
            )}
            name='additionalInformation'
            onChange={onCoshhInputChange}
            resizeVertical={true}
            tooltip={
              <>
                <p>{i18nT('controlMeasuresTooltip.description')}</p>
                <ul className='tw-pl-[15px]'>
                  <li>{i18nT('controlMeasuresTooltip.item1')}</li>
                  <li>{i18nT('controlMeasuresTooltip.item2')}</li>
                  <li>{i18nT('controlMeasuresTooltip.item3')}</li>
                  <li>{i18nT('controlMeasuresTooltip.item4')}</li>
                  <li>{i18nT('controlMeasuresTooltip.item5')}</li>
                </ul>
              </>
            }
            tooltipClassName='tooltip-dark--text-left'
            value={additionalInformation}
          />
        </div>
      </div>
    </div>
  );
}

CoshhForm.propTypes = {
  coshhDocument: PropTypes.object.isRequired,
  onCoshhInputChange: PropTypes.func.isRequired,
  onCoshhOptionChange: PropTypes.func.isRequired,
};
